import { Box, VStack, Flex, Text, Image } from "@chakra-ui/react";
import { useLocation, useNavigate } from "react-router-dom";
import { colors } from "../../theme";
import AppButton from "../AppButton";
import {
  DashboardIcon,
  MallIcon,
  RestaurantIcon,
  UsersIcon,
  PromoIcon,
  SettingsIcon,
  LogoIcon,
  SidebarIcon,
  OrdersIcon,
  CalendarIcon,
  MenuIcon,
} from "../../utils/svg";
import { useAdminRole } from "../../utils/useAdminRole";
import { useCategoryRequests } from "../../services/useQueries";

const SIDEBAR_WIDTH = "250px";

// Navigation items configuration. `superAdminOnly` items are hidden from
// MALL_ADMIN accounts (GAP-018) — their data is scoped to one mall and they
// cannot manage malls or provision other admins.
const navItems = [
  {
    path: "/dashboard",
    label: "Dashboard",
    icon: DashboardIcon,
  },
  {
    path: "/orders",
    label: "Orders",
    icon: OrdersIcon,
  },
  {
    path: "/mall-management",
    label: "Mall Management",
    icon: MallIcon,
    superAdminOnly: true,
  },
  {
    path: "/mall-admins",
    label: "Mall Admins",
    icon: MallIcon,
    superAdminOnly: true,
  },
  {
    path: "/restaurant-management",
    label: "Shops",
    icon: RestaurantIcon,
  },
  {
    path: "/categories",
    label: "Categories",
    icon: MenuIcon,
    superAdminOnly: true,
    // Shows how many shop suggestions are waiting.
    countKey: "categoryRequests",
  },
  {
    path: "/subscriptions",
    label: "Subscriptions",
    icon: CalendarIcon,
    superAdminOnly: true,
  },
  {
    path: "/user-management",
    label: "User Management",
    icon: UsersIcon,
  },
  {
    path: "/promo-code-management",
    label: "Promo Code Management",
    icon: PromoIcon,
  },
  {
    path: "/settings",
    label: "Settings",
    icon: SettingsIcon,
  },
];

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isSuperAdmin } = useAdminRole();
  const { data: categoryRequests } = useCategoryRequests({
    enabled: !!isSuperAdmin,
    refetchInterval: 60 * 1000,
  });
  const counts = { categoryRequests: categoryRequests?.waiting?.length || 0 };

  const visibleNavItems = navItems.filter(
    (item) => !item.superAdminOnly || isSuperAdmin
  );

  const isActive = (path) => {
    return (
      location.pathname === path || location.pathname.startsWith(path + "/")
    );
  };

  return (
    <Box
      as="aside"
      w={SIDEBAR_WIDTH}
      minW={SIDEBAR_WIDTH}
      bg={colors.sidebar.bg}
      borderRadius={16}
      display="flex"
      flexDirection="column"
      border="1px solid"
      borderColor={colors.disabled}
    >
      {/* Logo Section */}
      <Flex
        align="center"
        justify="space-between"
        py={6}
        px={4}
        borderBottom="1px solid"
        borderColor="rgba(255, 255, 255, 0.1)"
      >
       
         
          <Image
            src={`${process.env.PUBLIC_URL}/brand/mallbuddy-logo-horizontal.png`}
            alt="MallBuddy"
            h="64px"
            w="auto"
          />
           <SidebarIcon  />
       
      </Flex>

      {/* Navigation Items */}
      <VStack
        flex={1}
        py={4}
        px={3}
        spacing={1}
        align="stretch"
        overflowY="auto"
      >
        {visibleNavItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);

          return (
            <Box
              key={item.path}
              as="button"
              onClick={() => navigate(item.path)}
              display="flex"
              alignItems="center"
              gap={3}
              w="100%"
              px={4}
              py={3}
              borderRadius="md"
              bg={active ? "rgba(255, 83, 9, 0.15)" : "transparent"}
              color={active ? colors.primary : colors.sidebar.textMuted}
              fontWeight={active ? "600" : "400"}
              fontSize="sm"
              transition="all 0.2s"
              _hover={{
                bg: active ? "rgba(255, 83, 9, 0.2)" : colors.sidebar.hover,
                color: active ? colors.primary : colors.sidebar.text,
              }}
              textAlign="left"
            >
              <Icon boxSize={5} color={active ? colors.primary : colors.sidebar.textMuted} />
              <Text>{item.label}</Text>
              {item.countKey && counts[item.countKey] > 0 && (
                <Box
                  ml="auto"
                  minW="20px"
                  px={1.5}
                  borderRadius="full"
                  bg={colors.primary}
                  color="white"
                  fontSize="xs"
                  fontWeight="600"
                  textAlign="center"
                  aria-label={`${counts[item.countKey]} waiting`}
                >
                  {counts[item.countKey]}
                </Box>
              )}
            </Box>
          );
        })}
      </VStack>

      {/* Footer/Version */}
      <Box
        py={4}
        px={4}
        borderTop="1px solid"
        borderColor="rgba(255, 255, 255, 0.1)"
      >
        <Text fontSize="xs" color={colors.sidebar.textMuted} textAlign="center">
          MallBuddy Admin v1.0.0
        </Text>
      </Box>
    </Box>
  );
};

export { SIDEBAR_WIDTH };
export default Sidebar;
